/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { ROLES } from "../../../constants/roles";
import { getClientes } from "../../../utils";
import { getClientesPorBroker } from "../../../utils/cotizacionesGuardadas";

/**
 * Hook que gestiona la carga de clientes con filtro por rol/ejecutivo.
 */
export function useClientesData({ pais, rol, userId, agencia }) {
  const [clientes, setClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setClientesLoading(true);
      try {
        const [clientesData] = await Promise.all([getClientes({ pais })]);
        let filteredClientes = clientesData;
        if (rol === ROLES.BROKER) {
          // BROKER: solo su cartera (broker_id = agencia), en AMBOS países (incluido Perú,
          // a diferencia del filtro de comercial que se saltea en Perú).
          // FAIL-SAFE restrictivo INTENCIONAL: si no tiene agencia o la cartera no resuelve,
          // el dropdown queda VACÍO (no puede cotizar hasta tener cartera). Mejor vacío que
          // exponer cartera ajena — un fallo del scoping cierra el acceso, no lo abre.
          let carteraIds = new Set();
          try {
            const cartera = await getClientesPorBroker(pais, agencia);
            carteraIds = new Set((cartera || []).map((c) => Number(c.id)));
          } catch {
            carteraIds = new Set();
          }
          filteredClientes = clientesData.filter((cliente) => carteraIds.has(Number(cliente.id)));
        } else if (pais !== "Perú") {
          if (rol === "comercial") {
            filteredClientes = clientesData.filter((cliente) => {
              const esPrincipal = String(cliente.ejecutivo) === String(userId);
              const asignados = cliente.ejecutivos_asignados || [];
              const estaEnAsignados = asignados.map(String).includes(String(userId));
              return esPrincipal || estaEnAsignados;
            });
          } else if (rol === "gerencia" || rol === "superadmin") {
            filteredClientes = clientesData;
          }
        }
        setClientes(filteredClientes);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error al obtener datos:", error);
        }
        toast.error("No se pudieron cargar los datos.");
      } finally {
        setClientesLoading(false);
      }
    };
    fetchData();
  }, [pais, rol, userId, agencia]);

  const renderClientesOptions = () => {
    // value = cliente.id (regla 10.7: la relación se resuelve por ID, nunca por nombre).
    // El nombre queda solo como label de display. No se deduplica por nombre: cada cliente
    // es una opción por su id único (evita colapsar homónimos en una sola opción ambigua).
    const clientesOrdenados = [...clientes].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
    return clientesOrdenados.map((cliente) => (
      <option key={cliente.id} value={cliente.id}>
        {cliente.nombre}
      </option>
    ));
  };

  return {
    clientes,
    clientesLoading,
    renderClientesOptions,
  };
}
